import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Copy } from "lucide-react";
import { DeliveryModeV2 } from "@/types/program-v2";

interface CopyFromProgramTypeProps {
  currentStep: string;
  activeProgramType: DeliveryModeV2;
  programTypes: Record<string, any>;
  onCopy: (sourceType: DeliveryModeV2, stepData: any) => void;
}

export const CopyFromProgramType: React.FC<CopyFromProgramTypeProps> = ({
  currentStep,
  activeProgramType,
  programTypes,
  onCopy,
}) => {
  const [showDialog, setShowDialog] = useState(false);
  const [selectedSourceType, setSelectedSourceType] = useState<DeliveryModeV2 | null>(null);

  // Get available program types to copy from (exclude current active type)
  const availableTypes = Object.keys(programTypes)
    .filter(type => type !== activeProgramType) as DeliveryModeV2[];

  // Hide component if no other program types exist
  if (availableTypes.length === 0) {
    return null;
  }

  const handleCopyClick = (sourceType: DeliveryModeV2) => {
    setSelectedSourceType(sourceType);
    setShowDialog(true);
  };

  const handleConfirmCopy = () => {
    if (selectedSourceType) {
      const sourceData = programTypes[selectedSourceType];
      onCopy(selectedSourceType, sourceData);
      setShowDialog(false);
      setSelectedSourceType(null);
    }
  };

  const getStepDisplayName = (step: string) => {
    switch (step) {
      case 'content': return 'Content';
      case 'pricing': return 'Pricing';
      case 'schedule': return 'Schedule';
      case 'marketing': return 'Marketing';
      default: return step;
    }
  };

  const getProgramTypeDisplayName = (type: DeliveryModeV2) => {
    switch (type) {
      case 'hybrid': return 'Hybrid';
      case 'via-zoom': return 'Via-Zoom';
      case 'on-campus': return 'On-Campus';
      case 'online': return 'Online';
      default: return type;
    }
  };

  return (
    <>
      <div className="flex gap-2">
        {availableTypes.map(type => (
          <Button
            key={type}
            variant="outline"
            size="sm"
            onClick={() => handleCopyClick(type)}
            className="flex items-center gap-1"
          >
            <Copy className="h-3 w-3" />
            Copy from {getProgramTypeDisplayName(type)}
          </Button>
        ))}
      </div>

      <AlertDialog open={showDialog} onOpenChange={setShowDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Copy {getStepDisplayName(currentStep)} Data</AlertDialogTitle>
            <AlertDialogDescription>
              This will overwrite existing {getStepDisplayName(currentStep).toLowerCase()} data for {getProgramTypeDisplayName(activeProgramType)} with data from {selectedSourceType ? getProgramTypeDisplayName(selectedSourceType) : ''}. Continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmCopy}>
              Copy
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
